/**
 * FIE Context — SINGLE TRUST BOUNDARY
 * 
 * All financial data passes through maskForecastForRole() before leaving this provider.
 * No consumer component can access raw, unmasked forecast state.
 * UI components render what they are given — they do NOT decide what is safe.
 *
 * INTEGRITY GUARANTEES:
 * - No fallback snapshot IDs (no "inherited" or placeholder)
 * - Auto-snapshot creation is synchronous and deterministic
 * - Locked periods ALWAYS have a real snapshot before forecast generation
 * - Raw forecast is PRIVATE — only masked outputs are exposed
 */
import React, { createContext, useContext, useMemo, useState, useCallback, ReactNode } from "react";
import { useAppData } from "../AppDataContext";
import { useRole } from "../RoleContext";
import { generateForecast, calculateDistributions, buildReconciliation, maskForecastForRole } from "./engine";
import { getFIEPermissions } from "./permissions";
import { getDefaultAssumptions, getDefaultPartners, getDefaultPeriods, getSeedAuditLog } from "./seedAssumptions";
import { isDev } from "@/utils/devOnly";
import type {
  PeriodForecast, PartnerDistribution, Assumption, Partner,
  ReconciliationRecord, FIEAuditEntry, ForecastPeriod,
  AssumptionSnapshot, MaskedForecast,
} from "./types";

// ── Role-Safe Context API ──
// NO raw PeriodForecast. NO raw distributions. Everything is pre-masked.
interface FIEContextValue {
  // Navigation / period selection
  periods: ForecastPeriod[];
  selectedPeriodId: string;
  setSelectedPeriodId: (id: string) => void;
  isPeriodLocked: (periodId: string) => boolean;

  // ROLE-SAFE outputs — already filtered through maskForecastForRole
  visibleForecast: MaskedForecast;
  visibleDistributions: PartnerDistribution[];
  visiblePartners: Partner[];
  visibleReconciliation: ReconciliationRecord[];

  // Assumptions — editable only with proper permissions (enforced in UI + audit)
  assumptions: Assumption[];
  setAssumptions: React.Dispatch<React.SetStateAction<Assumption[]>>;

  // Audit & governance
  auditLog: FIEAuditEntry[];
  addAuditEntry: (entry: FIEAuditEntry) => void;
  assumptionSnapshots: AssumptionSnapshot[];
  snapshotAssumptions: (periodId: string, source: "auto" | "manual") => string;
  getAssumptionsForPeriod: (periodId: string) => Assumption[];

  // Audit helpers
  logDistributionViewed: (partnerId: string, userId: string, userRole: string) => void;
  logRestrictedAccess: (action: string, userId: string, userRole: string, detail: string) => void;

  // Permission metadata (so components know what to render, not what data is safe)
  canViewAllPartners: boolean;
  canViewOwnOnly: boolean;
  canViewCosts: boolean;
  canViewEconomics: boolean;
  canViewAudit: boolean;
  canViewReconciliation: boolean;
  canEditAssumptions: boolean;
  canEditProtected: boolean;
}

const FIEContext = createContext<FIEContextValue | null>(null);

export function FIEProvider({ children }: { children: ReactNode }) {
  const { jobs } = useAppData();
  const { role } = useRole();
  const perms = getFIEPermissions(role);

  const [assumptions, setAssumptions] = useState<Assumption[]>(isDev ? getDefaultAssumptions() : []);
  const [auditLog, setAuditLog] = useState<FIEAuditEntry[]>(isDev ? getSeedAuditLog() : []);
  const [assumptionSnapshots, setAssumptionSnapshots] = useState<AssumptionSnapshot[]>([]);
  const partners = useMemo(() => isDev ? getDefaultPartners() : [], []);
  const periods = useMemo(() => isDev ? getDefaultPeriods() : [], []);
  const [selectedPeriodId, setSelectedPeriodId] = useState(periods[periods.length - 1].id);

  const selectedPeriod = useMemo(
    () => periods.find(p => p.id === selectedPeriodId) || periods[periods.length - 1],
    [selectedPeriodId, periods]
  );

  const addAuditEntry = useCallback((entry: FIEAuditEntry) => {
    setAuditLog(prev => [entry, ...prev]);
  }, []);

  const isPeriodLocked = useCallback((periodId: string): boolean => {
    const period = periods.find(p => p.id === periodId);
    return period?.status === "closed" || period?.status === "reconciled";
  }, [periods]);

  const snapshotAssumptions = useCallback((periodId: string, source: "auto" | "manual"): string => {
    const existing = assumptionSnapshots.find(s => s.periodId === periodId);
    if (existing) return existing.id;

    const id = `SNAP-${periodId}-${Date.now()}`;
    const snapshot: AssumptionSnapshot = {
      id,
      periodId,
      createdAt: new Date().toISOString(),
      source,
      immutable: true,
      assumptions: JSON.parse(JSON.stringify(assumptions)),
    };
    setAssumptionSnapshots(prev => [...prev, snapshot]);

    const auditAction = source === "auto" ? "snapshot_auto_created" : "assumption_snapshot_created";
    addAuditEntry({
      id: `AUD-${Date.now()}`,
      timestamp: new Date().toISOString(),
      userId: "SYSTEM",
      userName: "System",
      userRole: "system",
      action: auditAction,
      objectType: "snapshot",
      objectId: id,
      objectLabel: `Snapshot for ${periodId}`,
      oldValue: "",
      newValue: id,
      reason: source === "auto" ? "Auto-created: locked period requires immutable snapshot" : "Manually created",
      effectiveDate: new Date().toISOString().split("T")[0],
      periodId,
      snapshotId: id,
    });

    return id;
  }, [assumptions, assumptionSnapshots, addAuditEntry]);

  const getAssumptionsForPeriod = useCallback((periodId: string): Assumption[] => {
    const snapshot = assumptionSnapshots
      .filter(s => s.periodId === periodId)
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];
    return snapshot ? snapshot.assumptions : assumptions;
  }, [assumptionSnapshots, assumptions]);

  const resolveSnapshot = useCallback((period: ForecastPeriod): { snapshotId: string; createdAt?: string; source?: "auto" | "manual"; assumptions: Assumption[] } => {
    const isLocked = period.status === "closed" || period.status === "reconciled";

    if (!isLocked) {
      return { snapshotId: "SNAP-live", assumptions };
    }

    let existing = assumptionSnapshots.find(s => s.periodId === period.id);

    if (!existing) {
      const newId = snapshotAssumptions(period.id, "auto");
      existing = assumptionSnapshots.find(s => s.id === newId);

      if (!existing) {
        return {
          snapshotId: newId,
          createdAt: new Date().toISOString(),
          source: "auto",
          assumptions: JSON.parse(JSON.stringify(assumptions)),
        };
      }
    }

    return {
      snapshotId: existing.id,
      createdAt: existing.createdAt,
      source: existing.source,
      assumptions: existing.assumptions,
    };
  }, [assumptions, assumptionSnapshots, snapshotAssumptions]);

  const snapshotResolution = useMemo(() => resolveSnapshot(selectedPeriod), [selectedPeriod, resolveSnapshot]);

  // ── PRIVATE: raw forecast — never leaves this provider ──
  const rawForecast: PeriodForecast = useMemo(
    () => generateForecast(
      jobs,
      selectedPeriod,
      snapshotResolution.assumptions,
      snapshotResolution.snapshotId,
      snapshotResolution.createdAt,
      snapshotResolution.source,
    ),
    [jobs, selectedPeriod, snapshotResolution]
  );

  // ── PRIVATE: raw distributions ──
  const rawDistributions = useMemo(
    () => calculateDistributions(rawForecast, partners),
    [rawForecast, partners]
  );

  // ── ROLE-SAFE: masked forecast — the ONLY financial data that leaves the provider ──
  const visibleForecast: MaskedForecast = useMemo(() => {
    if (!perms) {
      // No FIE access at all — minimal safe shell
      return {
        period: rawForecast.period,
        confidence: rawForecast.confidence,
        completeness: rawForecast.completeness,
        proof: rawForecast.proof,
        integrityStatus: rawForecast.integrityStatus,
        generatedAt: rawForecast.generatedAt,
      };
    }
    return maskForecastForRole(rawForecast, perms, undefined, rawDistributions);
  }, [rawForecast, perms, rawDistributions]);

  // ── ROLE-SAFE: filtered distributions ──
  const visibleDistributions: PartnerDistribution[] = useMemo(() => {
    if (!perms) return [];
    if (perms.viewAllPartnerProjections) return rawDistributions;
    if (perms.viewOwnProjectionOnly) return rawDistributions.slice(0, 1); // own row only
    return [];
  }, [rawDistributions, perms]);

  // ── ROLE-SAFE: filtered partners ──
  const visiblePartners: Partner[] = useMemo(() => {
    if (!perms) return [];
    if (perms.viewAllPartnerProjections) return partners;
    if (perms.viewOwnProjectionOnly) return partners.slice(0, 1);
    return [];
  }, [partners, perms]);

  // ── ROLE-SAFE: filtered reconciliation ──
  const visibleReconciliation: ReconciliationRecord[] = useMemo(() => {
    if (!perms?.viewReconciliation) return [];
    const closedPeriods = periods.filter(p => p.status === "closed" || p.status === "reconciled");
    return closedPeriods.map(period => {
      const resolution = resolveSnapshot(period);
      return buildReconciliation(jobs, [period], resolution.assumptions, resolution.snapshotId);
    }).flat();
  }, [jobs, periods, resolveSnapshot, perms]);

  // ── Permission flags (display hints, NOT security gates) ──
  const canViewAllPartners = perms?.viewAllPartnerProjections ?? false;
  const canViewOwnOnly = (perms?.viewOwnProjectionOnly && !canViewAllPartners) ?? false;
  const canViewCosts = perms?.viewCostBreakdown ?? false;
  const canViewEconomics = perms?.viewCompanyWideEconomics ?? false;
  const canViewAudit = perms?.viewAuditLog ?? false;
  const canViewReconciliation = perms?.viewReconciliation ?? false;
  const canEditAssumptions = perms?.editAssumptions ?? false;
  const canEditProtected = perms?.editProtectedAssumptions ?? false;

  const logDistributionViewed = useCallback((partnerId: string, userId: string, userRole: string) => {
    addAuditEntry({
      id: `AUD-${Date.now()}-dv`,
      timestamp: new Date().toISOString(),
      userId, userName: userId, userRole,
      action: "distribution_viewed",
      objectType: "distribution",
      objectId: partnerId,
      objectLabel: `Distribution for ${partnerId}`,
      oldValue: "", newValue: "",
      reason: "Partner distribution details accessed",
      effectiveDate: new Date().toISOString().split("T")[0],
      periodId: selectedPeriodId,
      metadata: { partnerId },
    });
  }, [addAuditEntry, selectedPeriodId]);

  const logRestrictedAccess = useCallback((action: string, userId: string, userRole: string, detail: string) => {
    addAuditEntry({
      id: `AUD-${Date.now()}-ra`,
      timestamp: new Date().toISOString(),
      userId, userName: userId, userRole,
      action: "restricted_access_attempt",
      objectType: "system",
      objectId: action,
      objectLabel: detail,
      oldValue: "", newValue: "",
      reason: `Attempted restricted action: ${detail}`,
      effectiveDate: new Date().toISOString().split("T")[0],
      metadata: { blockedAction: action },
    });
  }, [addAuditEntry]);

  return (
    <FIEContext.Provider value={{
      periods, selectedPeriodId, setSelectedPeriodId, isPeriodLocked,
      visibleForecast, visibleDistributions, visiblePartners, visibleReconciliation,
      assumptions, setAssumptions,
      auditLog, addAuditEntry,
      assumptionSnapshots, snapshotAssumptions, getAssumptionsForPeriod,
      logDistributionViewed, logRestrictedAccess,
      canViewAllPartners, canViewOwnOnly, canViewCosts, canViewEconomics,
      canViewAudit, canViewReconciliation, canEditAssumptions, canEditProtected,
    }}>
      {children}
    </FIEContext.Provider>
  );
}

export function useFIE() {
  const ctx = useContext(FIEContext);
  if (!ctx) throw new Error("useFIE must be used inside FIEProvider");
  return ctx;
}
